import { arraysEqual } from "../utils/utils";
import { Color } from "./color.model";
import { randomHex } from "../utils/color.utils";

export type LightningPattern = {
  outside: Color;
  middle: Color;
  inside: Color;
};

export type LightningPatternKey = keyof LightningPattern;

export const randomLightningPattern = (): LightningPattern => ({
  outside: randomHex(),
  middle: randomHex(),
  inside: randomHex(),
});

export type TwistedStripePattern = {
  stripe1: Color;
  stripe2: Color;
  stripe3: Color;
  stripe4: Color;
  stripe5: Color;
}

export type TwistedStripePatternKey = keyof TwistedStripePattern;

export const randomTwistedStripePattern = (): TwistedStripePattern => ({
  stripe1: randomHex(),
  stripe2: randomHex(),
  stripe3: randomHex(),
  stripe4: randomHex(),
  stripe5: randomHex(),
});

export type PatternType = "lightning" | "twisted-stripe";
export type Pattern = LightningPattern | TwistedStripePattern;
export type PatternKey = LightningPatternKey | TwistedStripePatternKey;

// Mirrors the `patterns` table's columns exactly — the shape Supabase query
// results are typed as, before any app-side parsing (colors as a flat array,
// snake_case column names).
export interface PatternRecord {
  id: string;
  user_id: string;
  pattern_type: PatternType;
  colors: Color[];
  created_at: string;
  updated_at: string;
  name: string | null;
  archived_at: string | null;
  collection_id: string | null;
}

// The non-color fields the app tracks about a stored pattern, in app-facing
// (camelCase) form — the PatternRecord columns other than pattern_type/colors,
// which live on Pattern instead.
export interface PatternAttributes {
  id: string;
  userId: string;
  name: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  collectionId: string | null;
}

// A pattern that has been persisted to (or loaded from) Supabase: its color
// data plus its stored attributes. Kept as a wrapper rather than folded into
// Pattern itself since a pattern being newly created client-side has no
// attributes until saved.
export type StoredPattern = Pattern & PatternAttributes;

// in the order they should be presented on screen
export const keysForPattern = (type: PatternType): PatternKey[] => {
  switch (type) {
    case "lightning":
      return ["outside", "middle", "inside"];
    case "twisted-stripe":
      return ["stripe1", "stripe2", "stripe3", "stripe4", "stripe5"];
  }
};

export const patternToColors = (pattern: Pattern, type: PatternType): Color[] => {
  return keysForPattern(type).map(key => (pattern as Record<PatternKey, Color>)[key]);
};

export const colorsToPattern = (colors: Color[], type: PatternType): Pattern => {
  const keys = keysForPattern(type);
  return Object.fromEntries(keys.map((key, i) => [key, colors[i]])) as Pattern;
};

export const rowToStoredPattern = (row: PatternRecord): StoredPattern => ({
  ...colorsToPattern(row.colors, row.pattern_type),
  id: row.id,
  userId: row.user_id,
  name: row.name,
  archivedAt: row.archived_at,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  collectionId: row.collection_id,
});

export const isLightningPattern = (pattern: Pattern): pattern is LightningPattern => {
  return keysForPattern("lightning").every(key => Object.hasOwn(pattern, key));
};

export const isLightningPatternKey = (key: PatternKey): key is LightningPatternKey => {
  return keysForPattern("lightning").includes(key)
};

export const isTwistedStripePattern = (pattern: Pattern): pattern is TwistedStripePattern => {
  return keysForPattern("twisted-stripe").every(key => Object.hasOwn(pattern, key));
};

export const isTwistedStripePatternKey = (key: PatternKey): key is TwistedStripePatternKey => {
  return keysForPattern("twisted-stripe").includes(key);
};

export const dummyPattern = (type: PatternType): Pattern => {
  switch (type) {
    case "lightning":
      return {
        outside: randomHex(),
        middle: randomHex(),
        inside: randomHex(),
      }
    case "twisted-stripe":
      return {
        stripe1: randomHex(),
        stripe2: randomHex(),
        stripe3: randomHex(),
        stripe4: randomHex(),
        stripe5: randomHex(),
      }
  }
};

export const isSamePattern = (pattern1: Pattern, pattern2: Pattern, type: PatternType): boolean => {
  return arraysEqual(patternToColors(pattern1, type), patternToColors(pattern2, type));
}